import React from 'react';
import { Button, Stack } from '@mui/material';
import { useSelector } from 'react-redux';
import { Location, UserProfile } from '@trnsact/trnsact-shared-types';
import { getLocationOcaLink, getPersonalOcaLink } from 'utils/ocaLinkUtils';
import gql from 'graphql-tag';
import { useLazyQuery } from '@apollo/client';

const GET_LOCATIONS = gql`
  query locations {
    locations {
      locationId
      locationName
      category
      locationParentName
      childrenLocation {
        locationId
        locationName
      }
      address {
        addressId
        country
        address1
        address2
        city
        stateProvince
        postalCode
      }
    }
  }
`;

export const OcaLinksExportButtons: React.FC = () => {
  const account = useSelector((state: any) => state.account);
  const relatedUsers = useSelector((state: any) => state.relatedUsers) as UserProfile[];

  const [getLocationsData] = useLazyQuery(GET_LOCATIONS);

  const buildPersonalOcaLinks = () => {
    const usersCsvData = relatedUsers?.map(user => ({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      personalOcaLink: getPersonalOcaLink(user.dynamicsContactId!),
    }));

    const headers = ['First Name', 'Last Name', 'Email', 'Personal OCA Link'];

    const rows = usersCsvData.map(user =>
      [user.firstName, user.lastName, user.email, user.personalOcaLink].map(value => `"${value ?? ''}"`).join(',')
    );

    downloadCsv(headers, rows, `${account?.name} - personal_oca_links.csv`);
  };

  const buildLocationOcaLinks = async () => {
    const locationsData = await getLocationsData();

    const locationsCsvData = (locationsData?.data?.locations as Location[])?.map(loc => ({
      locationId: loc.locationId,
      name: loc.locationName,
      type: loc.category,
      parentLocation: loc.locationParentName,
      address: loc.address
        ? [
            [loc.address.address1, loc.address.address2].filter(Boolean).join(' '),
            loc.address.city,
            [loc.address.stateProvince, loc.address.postalCode].filter(Boolean).join(' '),
            loc.address.country,
          ]
            .filter(Boolean)
            .join(', ')
        : '',
      ocaLink: getLocationOcaLink(loc.locationId!, account.dynamicsAccountId!),
    }));

    const headers = [
      'Location ID',
      'Location Name',
      'Location Type',
      'Parent Location',
      'Address',
      'Location OCA Link',
    ];

    const rows = locationsCsvData.map(location =>
      [location.locationId, location.name, location.type, location.parentLocation, location.address, location.ocaLink]
        .map(value => `"${value ?? ''}"`)
        .join(',')
    );

    downloadCsv(headers, rows, `${account?.name} - location_oca_links.csv`);
  };

  const downloadCsv = (headers: string[], rows: string[], fileName: string) => {
    const csvContent = [headers.join(','), '', ...rows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);

    link.click();
    document.body.removeChild(link);
  };

  return (
    <Stack direction="row" spacing={2}>
      <Button variant="contained" onClick={() => buildPersonalOcaLinks()}>
        Personal OCA Links
      </Button>

      <Button variant="contained" onClick={() => buildLocationOcaLinks()}>
        Location OCA Links
      </Button>
    </Stack>
  );
};
